import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import ConfirmModal from '@/components/ConfirmModal';
import { Plus, FileText, Trash2, Edit, Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Download, CheckSquare, Square, CheckCheck, X, MoreVertical } from 'lucide-react';

function MotoristaSelect({ motoristas, value, onChange }) {
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);

  const selecionado = motoristas.find(m => m.id === value);
  const filtrados = motoristas.filter(m =>
    m.nome_razao.toLowerCase().includes(busca.toLowerCase())
  );

  useEffect(() => {
    function fechar(e) {
      if (ref.current && !ref.current.contains(e.target)) setAberto(false);
    }
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, []);

  function selecionar(id) {
    onChange(id);
    setAberto(false);
    setBusca('');
  }

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setAberto(o => !o)}
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mt-1 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-red-500 bg-white">
        <span className={selecionado ? 'text-slate-900' : 'text-slate-400'}>
          {selecionado ? selecionado.nome_razao : 'Selecione o motorista'}
        </span>
        <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
      </button>
      {aberto && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input autoFocus value={busca} onChange={e => setBusca(e.target.value)}
                placeholder="Pesquisar motorista..."
                className="w-full pl-7 pr-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500" />
            </div>
          </div>
          <ul className="max-h-48 overflow-y-auto py-1">
            {filtrados.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-400">Nenhum motorista encontrado</li>
            )}
            {filtrados.map(m => (
              <li key={m.id} onClick={() => selecionar(m.id)}
                className={`px-3 py-2 text-sm cursor-pointer hover:bg-red-50 hover:text-red-700 ${value === m.id ? 'bg-red-50 text-red-700 font-medium' : 'text-slate-800'}`}>
                {m.nome_razao}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const EMPTY_EMPRESA = {
  dinheiro: '', pix: '', cartao: '', pct_cartao: '3.5', voucher: '', taxa_voucher: '3.5',
};

const toFloat = (v) => parseFloat(v) || 0;

function calcular(dados, percentual) {
  const cartao = toFloat(dados.cartao);
  const pct_cartao = toFloat(dados.pct_cartao) || 3.5;
  const pct_cartao_valor = cartao * pct_cartao / 100;
  const cartao_liquido = cartao - pct_cartao_valor;
  const voucher = toFloat(dados.voucher);
  const taxa_voucher = toFloat(dados.taxa_voucher) || 3.5;
  const taxa_voucher_valor = voucher * taxa_voucher / 100;
  const voucher_liquido = voucher - taxa_voucher_valor;
  const total = toFloat(dados.dinheiro) + toFloat(dados.pix) + cartao_liquido + voucher_liquido;
  const valor_motorista = total * (toFloat(percentual) / 100);
  const valor_empresa = total - valor_motorista;
  return { total, valor_motorista, valor_empresa, cartao_liquido, pct_cartao_valor, taxa_voucher_valor, voucher_liquido };
}

function moeda(v) {
  return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function EmpresaForm({ label, color, dados, onChange, percentual }) {
  const calc = calcular(dados, percentual);
  const campos = [
    { key: 'dinheiro', label: 'Dinheiro' },
    { key: 'pix', label: 'Pix' },
    { key: 'cartao', label: 'Cartão' },
    { key: 'pct_cartao', label: 'Taxa Cartão (%)' },
    { key: 'voucher', label: 'Voucher (bruto)' },
  ];

  return (
    <div className={`border-2 rounded-xl p-4 ${color === 'green' ? 'border-green-200 bg-green-50/40' : 'border-red-200 bg-red-50/40'}`}>
      <h3 className={`font-bold text-base mb-3 ${color === 'green' ? 'text-green-700' : 'text-red-700'}`}>{label}</h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {campos.map(({ key, label: lbl }) => (
          <div key={key}>
            <Label className="text-xs text-slate-600">{lbl}</Label>
            <Input
              type="number" min="0" step="0.01" placeholder="0,00"
              value={dados[key]}
              onChange={e => onChange({ ...dados, [key]: e.target.value })}
              className="h-8 text-sm"
            />
          </div>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-slate-200">
        <div className="w-40">
          <Label className="text-xs text-slate-600">Taxa Voucher (%)</Label>
          <Input type="number" min="0" max="100" step="0.1" placeholder="3.5"
            value={dados.taxa_voucher}
            onChange={e => onChange({ ...dados, taxa_voucher: e.target.value })}
            className="h-8 text-sm"
          />
        </div>
      </div>

      {toFloat(dados.cartao) > 0 && (
        <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-2 text-xs text-blue-800">
          Cartão: {moeda(toFloat(dados.cartao))} − {toFloat(dados.pct_cartao) || 3.5}% = <strong>{moeda(calc.cartao_liquido)}</strong> (desconto {moeda(calc.pct_cartao_valor)})
        </div>
      )}
      {toFloat(dados.voucher) > 0 && (
        <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800">
          Voucher: {moeda(toFloat(dados.voucher))} − {toFloat(dados.taxa_voucher) || 3.5}% = <strong>{moeda(calc.voucher_liquido)}</strong> (desconto {moeda(calc.taxa_voucher_valor)})
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-3 gap-2 text-sm">
        <div className="bg-white rounded-lg p-2 border">
          <p className="text-xs text-slate-500">Total</p>
          <p className="font-bold text-slate-800">{moeda(calc.total)}</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-2 border border-blue-200">
          <p className="text-xs text-blue-600">Motorista ({percentual || 0}%)</p>
          <p className="font-bold text-blue-700">{moeda(calc.valor_motorista)}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2 border">
          <p className="text-xs text-slate-500">Empresa</p>
          <p className="font-bold text-slate-700">{moeda(calc.valor_empresa)}</p>
        </div>
      </div>
    </div>
  );
}

function AcertoCard({ acerto, onEdit, onDelete, onRecibo, selecionado, onSelect }) {
  const [expanded, setExpanded] = useState(false);

  const dataInicio = new Date(acerto.data_inicio + 'T00:00:00').toLocaleDateString('pt-BR');
  const dataFim = new Date(acerto.data_fim + 'T00:00:00').toLocaleDateString('pt-BR');
  const periodo = dataInicio === dataFim ? dataInicio : `${dataInicio} a ${dataFim}`;

  return (
    <div className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-colors ${selecionado ? 'border-red-400 ring-1 ring-red-300' : 'border-slate-200'}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            {onSelect && (
              <button type="button" onClick={() => onSelect(acerto.id)}
                className="mt-0.5 shrink-0 text-slate-400 hover:text-red-600">
                {selecionado ? <CheckSquare className="w-4 h-4 text-red-600" /> : <Square className="w-4 h-4" />}
              </button>
            )}
            <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-slate-900">{acerto.motorista_nome}</p>
              <Badge variant={acerto.status === 'pago' ? 'default' : 'secondary'}
                className={acerto.status === 'pago' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                {acerto.status === 'pago' ? 'Pago' : 'Pendente'}
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{periodo} · {acerto.percentual_motorista}%</p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs text-slate-500">Motorista</p>
            <p className="font-bold text-blue-700">{moeda(acerto.total_motorista)}</p>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
          <div className="bg-slate-50 rounded-lg p-2 text-center">
            <p className="text-xs text-slate-500">Total Geral</p>
            <p className="font-semibold text-slate-800">{moeda(acerto.total_geral)}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-2 text-center">
            <p className="text-xs text-green-600">BoraMais</p>
            <p className="font-semibold text-green-700">{moeda(acerto.boramais?.total)}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-2 text-center">
            <p className="text-xs text-red-600">BoraCar</p>
            <p className="font-semibold text-red-700">{moeda(acerto.boracar?.total)}</p>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
          {['boramais', 'boracar'].map((empresa) => {
            const d = acerto[empresa];
            const label = empresa === 'boramais' ? 'BoraMais' : 'BoraCar';
            const color = empresa === 'boramais' ? 'text-green-700' : 'text-red-700';
            return (
              <div key={empresa} className="text-sm">
                <p className={`font-semibold mb-1 ${color}`}>{label}</p>
                <div className="grid grid-cols-3 gap-1 text-xs text-slate-600">
                  {[['Dinheiro', d.dinheiro], ['Pix', d.pix], ['Cartão', d.cartao],
                    ['Taxa Cartão', d.pct_cartao], ['Voucher', d.voucher],
                    [`Tx Voucher (${d.taxa_voucher}%)`, d.taxa_voucher_valor],
                    ['Limpeza', d.limpeza], ['Recarga', d.recarga]].map(([lbl, val]) => (
                    <div key={lbl} className="bg-slate-50 rounded p-1">
                      <p className="text-slate-400">{lbl}</p>
                      <p className="font-medium">{moeda(val)}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-1 flex gap-2 text-xs">
                  <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded">Motorista: {moeda(d.valor_motorista)}</span>
                  <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded">Sobra: {moeda(d.sobra_total)}</span>
                </div>
              </div>
            );
          })}
          {acerto.observacoes && (
            <p className="text-xs text-slate-500 italic">Obs: {acerto.observacoes}</p>
          )}
        </div>
      )}

      <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between gap-2">
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-slate-500 flex items-center gap-1 hover:text-slate-700">
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? 'Ocultar' : 'Ver detalhes'}
        </button>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={() => onRecibo(acerto.id)}
            className="h-7 px-2 text-xs text-slate-600">
            <FileText className="w-3 h-3 mr-1" /> Recibo
          </Button>
          <Button size="sm" variant="outline" onClick={() => onEdit(acerto)}
            className="h-7 px-2 text-xs text-blue-600 border-blue-200 hover:bg-blue-50">
            <Edit className="w-3 h-3" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => onDelete(acerto)}
            className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50">
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AcertoMotoristas() {
  const [acertos, setAcertos] = useState([]);
  const [motoristas, setMotoristas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [selecionados, setSelecionados] = useState(new Set());
  const [modoSelecao, setModoSelecao] = useState(false);
  const [exportMenuAberto, setExportMenuAberto] = useState(false);
  const exportMenuRef = useRef(null);
  const [mobileMenuAberto, setMobileMenuAberto] = useState(false);
  const mobileMenuRef = useRef(null);
  const hoje = new Date();
  const [tipoPeriodo, setTipoPeriodo] = useState('mes'); // 'dia' | 'semana' | 'mes'
  const [dataRef, setDataRef] = useState(hoje);

  const [form, setForm] = useState({
    motorista_id: '',
    data_inicio: new Date().toISOString().split('T')[0],
    data_fim: new Date().toISOString().split('T')[0],
    percentual_motorista: '',
    boramais: { ...EMPTY_EMPRESA },
    boracar: { ...EMPTY_EMPRESA },
    limpeza: '',
    recarga: '',
    observacoes: '',
    status: 'pendente',
  });

  const carregar = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([
        api.get('/acertos'),
        api.get('/pessoas'),
      ]);
      setAcertos(a.data);
      setMotoristas(p.data.filter(p => p.motorista && p.ativo));
    } catch {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    const handler = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target))
        setExportMenuAberto(false);
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target))
        setMobileMenuAberto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleMotoristaChange = (id) => {
    const m = motoristas.find(m => m.id === id);
    setForm(f => ({
      ...f,
      motorista_id: id,
      percentual_motorista: m?.percentual_acerto ?? f.percentual_motorista,
    }));
  };

  const abrirNovo = () => {
    setEditando(null);
    setForm({
      motorista_id: '',
      data_inicio: new Date().toISOString().split('T')[0],
      data_fim: new Date().toISOString().split('T')[0],
      percentual_motorista: '',
      boramais: { ...EMPTY_EMPRESA },
      boracar: { ...EMPTY_EMPRESA },
      observacoes: '',
      status: 'pendente',
    });
    setShowForm(true);
  };

  const abrirEditar = (acerto) => {
    setEditando(acerto);
    const mapEmpresa = (d) => ({
      dinheiro: d.dinheiro || '',
      pix: d.pix || '',
      cartao: d.cartao || '',
      pct_cartao: d.pct_cartao ?? '3.5',
      voucher: d.voucher || '',
      taxa_voucher: d.taxa_voucher ?? '3.5',
    });
    // limpeza/recarga: suporte a registros antigos (campo dentro das empresas) e novos (campo raiz)
    const limpezaVal = acerto.limpeza != null ? acerto.limpeza
      : (toFloat(acerto.boramais?.limpeza) + toFloat(acerto.boracar?.limpeza)) || '';
    const recargaVal = acerto.recarga != null ? acerto.recarga
      : (toFloat(acerto.boramais?.recarga) + toFloat(acerto.boracar?.recarga)) || '';
    setForm({
      motorista_id: acerto.motorista_id,
      data_inicio: acerto.data_inicio,
      data_fim: acerto.data_fim,
      percentual_motorista: acerto.percentual_motorista,
      boramais: mapEmpresa(acerto.boramais),
      boracar: mapEmpresa(acerto.boracar),
      limpeza: limpezaVal,
      recarga: recargaVal,
      observacoes: acerto.observacoes || '',
      status: acerto.status,
    });
    setShowForm(true);
  };

  const salvar = async () => {
    if (!form.motorista_id) return toast.error('Selecione um motorista');
    if (!form.percentual_motorista) return toast.error('Informe o percentual do motorista');

    const payload = {
      motorista_id: form.motorista_id,
      data_inicio: form.data_inicio,
      data_fim: form.data_fim,
      percentual_motorista: toFloat(form.percentual_motorista),
      boramais: {
        dinheiro: toFloat(form.boramais.dinheiro),
        pix: toFloat(form.boramais.pix),
        cartao: toFloat(form.boramais.cartao),
        pct_cartao: toFloat(form.boramais.pct_cartao),
        voucher: toFloat(form.boramais.voucher),
        taxa_voucher: toFloat(form.boramais.taxa_voucher) || 3.5,
      },
      boracar: {
        dinheiro: toFloat(form.boracar.dinheiro),
        pix: toFloat(form.boracar.pix),
        cartao: toFloat(form.boracar.cartao),
        pct_cartao: toFloat(form.boracar.pct_cartao),
        voucher: toFloat(form.boracar.voucher),
        taxa_voucher: toFloat(form.boracar.taxa_voucher) || 3.5,
      },
      limpeza: toFloat(form.limpeza),
      recarga: toFloat(form.recarga),
      observacoes: form.observacoes,
      status: form.status,
    };

    try {
      if (editando) {
        await api.put(`/acertos/${editando.id}`, payload);
        toast.success('Acerto atualizado!');
      } else {
        await api.post('/acertos', payload);
        toast.success('Acerto registrado!');
      }
      setShowForm(false);
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar acerto');
    }
  };

  const excluir = async () => {
    try {
      await api.delete(`/acertos/${deleteTarget.id}`);
      toast.success('Acerto excluído');
      setDeleteTarget(null);
      carregar();
    } catch {
      toast.error('Erro ao excluir acerto');
    }
  };

  const gerarRecibo = (id) => {
    const toastId = toast.loading('Gerando recibo...');
    api.get(`/acertos/${id}/recibo`, { responseType: 'blob' })
      .then(({ data: blob }) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const primeiroNome = acertos.find(a => a.id === id)?.motorista_nome?.split(' ')[0] || id.slice(0, 8);
        const dataRaw = acertos.find(a => a.id === id)?.data_inicio || '';
        const dataAcerto = dataRaw ? dataRaw.split('-').reverse().join('') : '';
        a.download = `AcertoMotorista_${primeiroNome}_${dataAcerto}.pdf`;
        a.click();
        toast.success('Recibo gerado!', { id: toastId });
      })
      .catch(() => toast.error('Erro ao gerar recibo', { id: toastId }));
  };

  const MESES_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

  function navPeriodo(delta) {
    setDataRef(d => {
      const nova = new Date(d);
      if (tipoPeriodo === 'dia') nova.setDate(nova.getDate() + delta);
      else if (tipoPeriodo === 'semana') nova.setDate(nova.getDate() + delta * 7);
      else nova.setMonth(nova.getMonth() + delta);
      return nova;
    });
  }

  function getPeriodoBounds() {
    if (tipoPeriodo === 'dia') {
      const s = new Date(dataRef); s.setHours(0,0,0,0);
      const e = new Date(dataRef); e.setHours(23,59,59,999);
      return { inicio: s, fim: e };
    }
    if (tipoPeriodo === 'semana') {
      const dow = dataRef.getDay(); // 0=dom
      const s = new Date(dataRef); s.setDate(dataRef.getDate() - dow); s.setHours(0,0,0,0);
      const e = new Date(s); e.setDate(s.getDate() + 6); e.setHours(23,59,59,999);
      return { inicio: s, fim: e };
    }
    // mes
    const s = new Date(dataRef.getFullYear(), dataRef.getMonth(), 1);
    const e = new Date(dataRef.getFullYear(), dataRef.getMonth() + 1, 0, 23, 59, 59);
    return { inicio: s, fim: e };
  }

  function labelPeriodo() {
    const { inicio, fim } = getPeriodoBounds();
    const fmt = d => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    const fmtMes = d => d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    if (tipoPeriodo === 'dia') return dataRef.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
    if (tipoPeriodo === 'semana') return `${fmt(inicio)} – ${fmt(fim)}`;
    return MESES_PT[dataRef.getMonth()] + ' ' + dataRef.getFullYear();
  }

  function labelSubtitulo() {
    if (tipoPeriodo === 'dia') return dataRef.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    if (tipoPeriodo === 'semana') {
      const { inicio, fim } = getPeriodoBounds();
      return `${inicio.toLocaleDateString('pt-BR')} – ${fim.toLocaleDateString('pt-BR')}`;
    }
    return MESES_PT[dataRef.getMonth()] + ' ' + dataRef.getFullYear();
  }

  const { inicio: periodoInicio, fim: periodoFim } = getPeriodoBounds();

  const acertosFiltrados = acertos.filter(a => {
    const termoBusca = busca.toLowerCase();
    const matchBusca = !busca || a.motorista_nome.toLowerCase().includes(termoBusca);
    const matchStatus = !filtroStatus || a.status === filtroStatus;
    const dataAcerto = new Date((a.data_inicio || a.data_fim) + 'T12:00:00');
    const matchPeriodo = dataAcerto >= periodoInicio && dataAcerto <= periodoFim;
    return matchBusca && matchStatus && matchPeriodo;
  });

  // Agrupar por motorista
  const gruposPorMotorista = acertosFiltrados.reduce((acc, a) => {
    if (!acc[a.motorista_id]) acc[a.motorista_id] = { nome: a.motorista_nome, acertos: [] };
    acc[a.motorista_id].acertos.push(a);
    return acc;
  }, {});

  const totalMotoristas = acertosFiltrados.reduce((s, a) => s + a.total_motorista, 0);
  // limpeza/recarga: novos registros ficam na raiz; antigos ficam dentro das empresas
  const getLimpeza = (a) => a.limpeza != null ? toFloat(a.limpeza) : toFloat(a.boramais?.limpeza) + toFloat(a.boracar?.limpeza);
  const getRecarga = (a) => a.recarga != null ? toFloat(a.recarga) : toFloat(a.boramais?.recarga) + toFloat(a.boracar?.recarga);
  const totalEmpresa = acertosFiltrados.reduce((s, a) =>
    s + toFloat(a.boramais?.valor_empresa) + toFloat(a.boracar?.valor_empresa) - getLimpeza(a) - getRecarga(a), 0);
  const totalGeralAcertos = acertosFiltrados.reduce((s, a) => s + a.total_geral, 0);
  const totalLimpeza = acertosFiltrados.reduce((s, a) => s + getLimpeza(a), 0);
  const totalRecarga = acertosFiltrados.reduce((s, a) => s + getRecarga(a), 0);

  function toggleSelecionar(id) {
    setSelecionados(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelecionarGrupo(ids) {
    const todosNaSelecao = ids.every(id => selecionados.has(id));
    setSelecionados(prev => {
      const next = new Set(prev);
      ids.forEach(id => todosNaSelecao ? next.delete(id) : next.add(id));
      return next;
    });
  }

  async function marcarLoteStatus(status) {
    const ids = [...selecionados];
    const toastId = toast.loading(`Marcando ${ids.length} acerto(s) como ${status}...`);
    try {
      await api.put('/acertos/lote/status', { ids, status });
      toast.success(`${ids.length} acerto(s) marcado(s) como ${status}!`, { id: toastId });
      setSelecionados(new Set());
      setModoSelecao(false);
      carregar();
    } catch {
      toast.error('Erro ao atualizar acertos', { id: toastId });
    }
  }

  function exportar(formato) {
    const { inicio, fim } = getPeriodoBounds();
    const di = inicio.toISOString().split('T')[0];
    const df = fim.toISOString().split('T')[0];
    const toastId = toast.loading(`Gerando ${formato === 'excel' ? 'Excel' : 'PDF'}...`);
    api.get(`/acertos/export/${formato}?data_inicio=${di}&data_fim=${df}`, { responseType: 'blob' })
      .then(({ data: blob }) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const periodo = `${di.replace(/-/g,'')}_${df.replace(/-/g,'')}`;
        const ext = formato === 'excel' ? 'xlsx' : 'pdf';
        a.download = `Acertos_BoraCar_${periodo}.${ext}`;
        a.click();
        toast.success('Exportação concluída!', { id: toastId });
      })
      .catch((error) => {
        const msg = error.response?.status === 404 ? 'Nenhum acerto encontrado no período' : 'Erro ao exportar';
        toast.error(msg, { id: toastId });
      });
  }

  const calcBM = calcular(form.boramais, form.percentual_motorista);
  const calcBC = calcular(form.boracar, form.percentual_motorista);
  const totalGeral = calcBM.total + calcBC.total;
  const totalMotoristaPrev = calcBM.valor_motorista + calcBC.valor_motorista;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Acerto de Motoristas</h1>
          <p className="text-sm text-slate-500 mt-0.5">Fechamento diário ou semanal — BoraMais + BoraCar</p>
        </div>
        {/* Desktop: botões normais */}
        <div className="hidden sm:flex gap-2">
          <div className="relative" ref={exportMenuRef}>
            <button
              type="button"
              onClick={() => setExportMenuAberto(v => !v)}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 shadow-sm hover:bg-slate-50">
              <Download className="w-4 h-4" /> Exportar
              <ChevronDown className="w-3 h-3" />
            </button>
            {exportMenuAberto && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 min-w-[140px]">
                <button onClick={() => { exportar('excel'); setExportMenuAberto(false); }}
                  className="w-full px-4 py-2 text-sm text-left hover:bg-slate-50 text-slate-700 flex items-center gap-2">
                  <span>📊</span> Excel (.xlsx)
                </button>
                <button onClick={() => { exportar('pdf'); setExportMenuAberto(false); }}
                  className="w-full px-4 py-2 text-sm text-left hover:bg-slate-50 text-slate-700 flex items-center gap-2">
                  <span>📄</span> PDF (.pdf)
                </button>
              </div>
            )}
          </div>
          <Button variant="outline" onClick={() => { setModoSelecao(m => !m); setSelecionados(new Set()); }}
            className={modoSelecao ? 'bg-red-50 text-red-600 border-red-300' : 'text-slate-600 border-slate-200'}>
            <CheckCheck className="w-4 h-4 mr-2" /> {modoSelecao ? 'Cancelar' : 'Selecionar'}
          </Button>
          <Button onClick={abrirNovo} className="bg-gradient-to-r from-red-600 to-orange-600 text-white shadow">
            <Plus className="w-4 h-4 mr-2" /> Novo Acerto
          </Button>
        </div>

        {/* Mobile: botão + e menu ⋮ */}
        <div className="flex sm:hidden items-center gap-2">
          <button
            onClick={abrirNovo}
            className="flex items-center gap-1 rounded-md bg-gradient-to-r from-red-600 to-orange-600 text-white px-3 py-2 text-sm font-medium shadow">
            <Plus className="w-4 h-4" /> Novo
          </button>
          <div className="relative" ref={mobileMenuRef}>
            <button
              type="button"
              onClick={() => setMobileMenuAberto(v => !v)}
              className="p-2 rounded-md border border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-50">
              <MoreVertical className="w-5 h-5" />
            </button>
            {mobileMenuAberto && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 min-w-[180px]">
                <button onClick={() => { exportar('excel'); setMobileMenuAberto(false); }}
                  className="w-full px-4 py-3 text-sm text-left hover:bg-slate-50 text-slate-700 flex items-center gap-2 border-b border-slate-100">
                  <span>📊</span> Exportar Excel
                </button>
                <button onClick={() => { exportar('pdf'); setMobileMenuAberto(false); }}
                  className="w-full px-4 py-3 text-sm text-left hover:bg-slate-50 text-slate-700 flex items-center gap-2 border-b border-slate-100">
                  <span>📄</span> Exportar PDF
                </button>
                <button
                  onClick={() => { setModoSelecao(m => !m); setSelecionados(new Set()); setMobileMenuAberto(false); }}
                  className={`w-full px-4 py-3 text-sm text-left flex items-center gap-2 ${modoSelecao ? 'text-red-600 hover:bg-red-50' : 'text-slate-700 hover:bg-slate-50'}`}>
                  <CheckCheck className="w-4 h-4" /> {modoSelecao ? 'Cancelar Seleção' : 'Selecionar Acertos'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cards resumo */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Total Acertos</p>
          <p className="text-2xl font-bold text-slate-900">{acertosFiltrados.length}</p>
        </div>
        <div className="bg-purple-50 rounded-xl border border-purple-200 p-4">
          <p className="text-xs text-purple-600">Total Geral</p>
          <p className="text-xl font-bold text-purple-700">{moeda(totalGeralAcertos)}</p>
        </div>
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
          <p className="text-xs text-blue-600">Total Motoristas</p>
          <p className="text-xl font-bold text-blue-700">{moeda(totalMotoristas)}</p>
        </div>
        <div className="bg-green-50 rounded-xl border border-green-200 p-4">
          <p className="text-xs text-green-600">Total Empresa</p>
          <p className="text-xl font-bold text-green-700">{moeda(totalEmpresa)}</p>
        </div>
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Pendentes</p>
          <p className="text-2xl font-bold text-yellow-600">{acertos.filter(a => a.status === 'pendente').length}</p>
        </div>
        <div className="bg-cyan-50 rounded-xl border border-cyan-200 p-4">
          <p className="text-xs text-cyan-600">Total Limpeza</p>
          <p className="text-xl font-bold text-cyan-700">{moeda(totalLimpeza)}</p>
        </div>
        <div className="bg-orange-50 rounded-xl border border-orange-200 p-4">
          <p className="text-xs text-orange-600">Total Recarga</p>
          <p className="text-xl font-bold text-orange-700">{moeda(totalRecarga)}</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="space-y-3">
        {/* Seletor de tipo de período */}
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
          {[['dia','Dia'],['semana','Semana'],['mes','Mês']].map(([val, label]) => (
            <button key={val} onClick={() => { setTipoPeriodo(val); setDataRef(new Date()); }}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tipoPeriodo === val ? 'bg-white text-red-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Navegação do período */}
        <div className="flex gap-3 flex-wrap items-center">
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1.5">
            <button onClick={() => navPeriodo(-1)} className="p-1 rounded hover:bg-slate-100 text-slate-500">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold text-slate-800 min-w-[160px] text-center capitalize">
              {labelPeriodo()}
            </span>
            <button onClick={() => navPeriodo(1)} className="p-1 rounded hover:bg-slate-100 text-slate-500">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="relative flex-1 min-w-40">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Buscar motorista..." value={busca} onChange={e => setBusca(e.target.value)}
              className="pl-9" />
          </div>
          <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500">
            <option value="">Todos os status</option>
            <option value="pendente">Pendente</option>
            <option value="pago">Pago</option>
          </select>
        </div>
      </div>

      {/* Lista agrupada por motorista */}
      {acertosFiltrados.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">Nenhum acerto em {labelSubtitulo()}</p>
          <p className="text-sm">Clique em "Novo Acerto" para registrar o primeiro fechamento</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.values(gruposPorMotorista).map(grupo => {
            const totalMot = grupo.acertos.reduce((s, a) => s + a.total_motorista, 0);
            const totalEmp = grupo.acertos.reduce((s, a) => s + a.total_empresa, 0);
            const idsGrupo = grupo.acertos.map(a => a.id);
            const todosGrupoSelecionados = idsGrupo.every(id => selecionados.has(id));
            return (
              <div key={grupo.nome} className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                {/* Cabeçalho do motorista */}
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    {modoSelecao && (
                      <button type="button" onClick={() => toggleSelecionarGrupo(idsGrupo)}
                        className="text-slate-400 hover:text-red-600">
                        {todosGrupoSelecionados ? <CheckSquare className="w-4 h-4 text-red-600" /> : <Square className="w-4 h-4" />}
                      </button>
                    )}
                    <div>
                      <p className="font-bold text-slate-900">{grupo.nome}</p>
                      <p className="text-xs text-slate-500">{grupo.acertos.length} acerto{grupo.acertos.length > 1 ? 's' : ''} · {labelSubtitulo()}</p>
                    </div>
                  </div>
                  <div className="flex gap-4 text-right">
                    <div>
                      <p className="text-xs text-blue-600">Total Motorista</p>
                      <p className="font-bold text-blue-700 text-sm">{moeda(totalMot)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-green-600">Total Empresa</p>
                      <p className="font-bold text-green-700 text-sm">{moeda(totalEmp)}</p>
                    </div>
                  </div>
                </div>
                {/* Acertos do motorista */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 divide-y sm:divide-y-0 sm:gap-0 border-t border-slate-100">
                  {grupo.acertos.map((a, i) => (
                    <div key={a.id} className={`sm:border-r border-slate-100 last:border-r-0 ${i >= 2 ? 'sm:border-t border-slate-100' : ''}`}>
                      <AcertoCard acerto={a}
                        onEdit={abrirEditar} onDelete={setDeleteTarget} onRecibo={gerarRecibo}
                        selecionado={selecionados.has(a.id)}
                        onSelect={modoSelecao ? toggleSelecionar : null} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Barra flutuante de ações em lote */}
      {modoSelecao && selecionados.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-4 border border-slate-700">
          <span className="text-sm font-medium">{selecionados.size} selecionado{selecionados.size > 1 ? 's' : ''}</span>
          <div className="w-px h-5 bg-slate-600" />
          <button onClick={() => marcarLoteStatus('pago')}
            className="bg-green-600 hover:bg-green-500 text-white text-sm font-medium px-3 py-1.5 rounded-lg transition-colors">
            ✓ Marcar como Pago
          </button>
          <button onClick={() => marcarLoteStatus('pendente')}
            className="bg-yellow-600 hover:bg-yellow-500 text-white text-sm font-medium px-3 py-1.5 rounded-lg transition-colors">
            ⏳ Marcar Pendente
          </button>
          <button onClick={() => setSelecionados(new Set())}
            className="text-slate-400 hover:text-white p-1 rounded-lg transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Modal Formulário */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-4">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">
                {editando ? 'Editar Acerto' : 'Novo Acerto de Motorista'}
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">BoraMais + BoraCar no mesmo fechamento</p>
            </div>

            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Dados gerais */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label>Motorista *</Label>
                  <MotoristaSelect motoristas={motoristas} value={form.motorista_id} onChange={handleMotoristaChange} />
                </div>
                <div>
                  <Label>Percentual do Motorista (%) *</Label>
                  <Input type="number" min="0" max="100" step="0.1" placeholder="ex: 35"
                    value={form.percentual_motorista}
                    onChange={e => setForm(f => ({ ...f, percentual_motorista: e.target.value }))}
                    className="mt-1" />
                </div>
                <div>
                  <Label>Data Início *</Label>
                  <Input type="date" value={form.data_inicio}
                    onChange={e => setForm(f => ({ ...f, data_inicio: e.target.value }))}
                    className="mt-1" />
                </div>
                <div>
                  <Label>Data Fim *</Label>
                  <Input type="date" value={form.data_fim}
                    onChange={e => setForm(f => ({ ...f, data_fim: e.target.value }))}
                    className="mt-1" />
                </div>
              </div>

              {/* BoraMais */}
              <EmpresaForm label="BoraMais" color="green"
                dados={form.boramais} onChange={v => setForm(f => ({ ...f, boramais: v }))}
                percentual={form.percentual_motorista} />

              {/* BoraCar */}
              <EmpresaForm label="BoraCar" color="red"
                dados={form.boracar} onChange={v => setForm(f => ({ ...f, boracar: v }))}
                percentual={form.percentual_motorista} />

              {/* Limpeza e Recarga — campos únicos */}
              <div className="border-2 border-slate-200 rounded-xl p-4 bg-slate-50/40">
                <h3 className="font-bold text-base text-slate-700 mb-3">Descontos do Acerto</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-600">Limpeza (R$)</Label>
                    <Input type="number" min="0" step="0.01" placeholder="0,00"
                      value={form.limpeza}
                      onChange={e => setForm(f => ({ ...f, limpeza: e.target.value }))}
                      className="h-9 text-sm mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Recarga (R$)</Label>
                    <Input type="number" min="0" step="0.01" placeholder="0,00"
                      value={form.recarga}
                      onChange={e => setForm(f => ({ ...f, recarga: e.target.value }))}
                      className="h-9 text-sm mt-1" />
                  </div>
                </div>
              </div>

              {/* Preview total */}
              <div className="bg-slate-900 rounded-xl p-4 text-white">
                <p className="text-xs text-slate-400 mb-2 font-medium uppercase tracking-wide">Resumo do Fechamento</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <p className="text-xs text-slate-400">Total Geral</p>
                    <p className="text-lg font-bold">{moeda(totalGeral)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-300">Motorista</p>
                    <p className="text-lg font-bold text-blue-300">{moeda(totalMotoristaPrev)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Empresa (bruto)</p>
                    <p className="text-lg font-bold">{moeda(totalGeral - totalMotoristaPrev)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-green-300">Sobra Empresa</p>
                    <p className="text-lg font-bold text-green-300">
                      {moeda(totalGeral - totalMotoristaPrev - toFloat(form.limpeza) - toFloat(form.recarga))}
                    </p>
                  </div>
                </div>
                {(toFloat(form.limpeza) > 0 || toFloat(form.recarga) > 0) && (
                  <p className="text-xs text-slate-400 mt-2">
                    Descontos: Limpeza {moeda(toFloat(form.limpeza))} + Recarga {moeda(toFloat(form.recarga))} = {moeda(toFloat(form.limpeza) + toFloat(form.recarga))}
                  </p>
                )}
              </div>

              {/* Status e obs */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-red-500">
                    <option value="pendente">Pendente</option>
                    <option value="pago">Pago</option>
                  </select>
                </div>
                <div>
                  <Label>Observações</Label>
                  <Input placeholder="Opcional" value={form.observacoes}
                    onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))}
                    className="mt-1" />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button onClick={salvar} className="bg-gradient-to-r from-red-600 to-orange-600 text-white">
                {editando ? 'Salvar Alterações' : 'Registrar Acerto'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={excluir}
        title="Excluir Acerto"
        message={`Deseja excluir o acerto de "${deleteTarget?.motorista_nome}"? Esta ação não pode ser desfeita.`}
      />
    </div>
  );
}
